import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { firstValueFrom, from, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { AuthService } from 'src/app/core/auth.service';
import { StorageService } from 'src/app/shared-lib/core/storage';
import { Product, ProductService } from 'src/app/shared-lib/domains/product';
import { getFileFromEvent } from 'src/app/shared-lib/domains/upload';
import { UserService } from 'src/app/shared-lib/domains/user';
import { buildAnonymousUser, buildUserWithoutUid } from 'src/app/shared-lib/domains/user/user.helpers';
import { OrderStateService } from 'src/app/shared/order-state.service';

@Injectable({ providedIn: 'root' })
export class UserFlows {
  private readonly collectionName = 'users';
  private isInitialized = false;

  constructor(
    private storageService: StorageService,
    private userService: UserService,
    private productService: ProductService,
    private authService: AuthService,
    private cookieService: CookieService,
    private orderStateService: OrderStateService,
  ) { }

  readonly user$ = this.authService.authState$.pipe(
    switchMap(user => {
      if (!user) {
        this.authService.createAnonymousUser().then(() => { });
        return [buildUserWithoutUid()];
      }
      if (user.isAnonymous === true) {
        return [buildAnonymousUser(user.uid)];
      }
      return this.userService.getUserByUid$(user.uid);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly role$ = this.authService.authState$.pipe(
    switchMap(user => user ? from(user.getIdTokenResult()) : of(null)),
    map(idToken => idToken?.claims?.role ?? ''),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  init() {
    if (this.isInitialized) return;
    this.isInitialized = true;

    this.user$.subscribe(user => {
      this.userService.setCurrentUserState(user);
      this.userService.syncUserLegacyAddresses(user);
      this.syncLoginCookie(user);
    });
  }

  // TODO: Revisar si este logout está bien que se quede aquí
  async doLogout(): Promise<void> {
    await this.authService.doLogout();
    this.orderStateService.resetOrder();
  }

  doSignup(value: any) {
    const { email, password, ...data } = value;
    return this.authService.doRegister({ email, password })
      .then(user => {
        //eliminar password de data
        return this.userService.createUser({ ...user, ...data });
      })
      .catch();
  }

  async doSignupAnonym(value: any) {
    const { email, password, ...data } = value;
    return this.authService.doLinkEmail({ email, password })
      .then(user => {
        user.email = email;
        return this.userService.createUser({ ...user, ...data });
      })
      .catch();
  }

  async doEmailLogin(credential: any) {
    return this.authService.doEmailLogin(credential)
      .then(auth => this.userService.upsertUserLogin(auth.user));
  }

  async doSignupGoogle() {
    return this.authService.doLogin('google.com')
      .then((user) => {
        if (!user) {
          throw new Error('Google login did not return a user');
        }
        return this.userService.upsertUserLogin(user);
      });
  }

  doSignupFacebook() {
    return this.authService.doLogin('facebook.com')
      .then((user) => {
        if (!user) {
          throw new Error('Facebook login did not return a user');
        }
        return this.userService.upsertUserLogin(user);
      });
  }

  setPhotoProfile(event: any, userId: string) {
    const file: File = getFileFromEvent(event);
    if (!file) {
      console.error('File con problemas');
      return null;
    }
    const collectionPath = `${this.collectionName}/${userId}/files`;
    const storagePath = `${collectionPath}/profilePicture`;
    return firstValueFrom(
      this.storageService.uploadFile(file, storagePath, collectionPath, { docId: 'profilePicture' })
    );
  }

  async updatePhotoProfile(event: any, userId: string) {
    const result = await this.setPhotoProfile(event, userId);
    const url = result?.storedFile?.url;
    if (!url) return;
    this.userService.setUser({
      ...this.userService.userG,
      photoURL: url,
    });
  }

  /**
   * Returns a live stream of ambassador products for a given user.
   * Combines user domain (product IDs) with product domain (product data).
   * @param userId User ID.
   * @returns Observable emitting the active ambassador products.
   */
  ambassadorProducts$(userId: string): Observable<Product[]> {
    return this.userService.getAmbassadorProductIds$(userId).pipe(
      switchMap(ids => this.productService.getProductsByIds$(ids))
    );
  }

  private syncLoginCookie(user) {
    const loginCookie = this.cookieService.get('loginCopyfly');
    let pictureURL = user?.photoURL;
    if (user?.isAnonymous) {
      if (loginCookie) {
        this.cookieService.delete('loginCopyfly', '/', '.copyfly.es');
        this.cookieService.delete('nameLogin', '/', '.copyfly.es');
      }
    } else {
      if (!pictureURL?.includes('http')) {
        pictureURL = 'http://copyfly.es' + pictureURL?.substring(1);
      }
      if (!loginCookie || pictureURL !== loginCookie) {
        this.cookieService.set('loginCopyfly', pictureURL, 999, '/', '.copyfly.es');
        this.cookieService.set('nameLogin', user?.displayName, 999, '/', '.copyfly.es');
      }
    }
  }
}
